import { BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import {
  IFavouriteReportFromServer,
  IFavouriteReportResponse,
  IRecommendReportFromServer,
} from 'src/app/standaloneSlices/commonModels';
import { environment } from 'src/environment';
import { IAgeingPeriod } from 'src/feature/AgeingPeriod/AgeingPeriodModel';
import { IMenuItem } from 'src/feature/Menu/menuModels';
import { StaffFilterDetail } from 'src/services/reporting';
import {
  IGetStaffDetails,
  IPostReportSubscription,
  ReportSubscripitonsTemplate,
  UserReportSubscripitonsDTO,
} from 'src/services/subscription';
import { processMenuList, sortMenus } from 'src/Shared/Utilities/Menu';

export function transformServerMenuToLocalMenuStructure(menu: any[]): IMenuItem[] {
  const stringMenuData = JSON.stringify(menu);

  const replacedMenuData = JSON.parse(
    stringMenuData
      .replaceAll('MenuName', 'Name')
      .replaceAll('ReportName', 'Name')
      .replaceAll('ReportTitle', 'Title')
      .replaceAll('MenuTitle', 'Title'),
  );

  processMenuList(replacedMenuData, null);
  sortMenus(replacedMenuData);
  return replacedMenuData;
}

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' },
  ): BaseQueryFn<
    {
      url: string;
      method: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
      headers?: AxiosRequestConfig['headers'];
    },
    unknown,
    unknown
  > =>
  async ({ url, method, data, params, headers }) => {
    try {
      const finalUrl = url.startsWith('http') ? url : `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`;
      const result = await axios({
        url: finalUrl,
        method,
        data,
        params,
        headers,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const reportingApi = createApi({
  reducerPath: 'reportingApi',
  baseQuery: axiosBaseQuery({
    baseUrl: environment.reportingEndpoint,
  }),

  tagTypes: ['Menu', 'FavouriteReport', 'ReportSubscriptions', 'RecommendReport'],
  endpoints: (builder) => ({
    getAutoCompleteOptionList: builder.query<any[], string>({
      query: (url) => ({ url, method: 'GET' }),
      // transformResponse(rawResult: { data: IStaffWithActiveFlag[] }) {
      //     return rawResult.data;
      // }
    }),
    getAgeingPeriod: builder.query<IAgeingPeriod, string>({
      query: (url) => ({ url, method: 'GET' }),
    }),

    getFirmSubscriptions: builder.query<UserReportSubscripitonsDTO, { url: string; params?: any }>({
      query: ({ url, params }) => ({ url, method: 'GET', params }),
      providesTags: ['ReportSubscriptions'],
    }),
    getSubscriptionsTemplate: builder.query<ReportSubscripitonsTemplate, string>({
      query: (url) => ({ url, method: 'GET' }),
    }),
    createFirmSubscriptions: builder.mutation<IPostReportSubscription, any>({
      query: ({ ...data }) => ({
        url: '/api/v1/scheduled-reports/subscriptions',
        method: 'POST',
        data: data,
      }),
      invalidatesTags: ['ReportSubscriptions'],
    }),
    createSubscriptions: builder.mutation<IPostReportSubscription, any>({
      query: ({ path, ...data }) => ({
        url: `${environment.reportingServiceEndpoint}/api/v1/report-subscriptions/${path}`,
        method: 'POST',
        data: data,
      }),
      invalidatesTags: ['ReportSubscriptions'],
    }),
    deleteFirmSubscriptions: builder.mutation<string, any>({
      query: (url) => ({
        url: `${environment.reportingServiceEndpoint}/api/v1/report-subscriptions`,
        method: 'DELETE',
        params: {
          subscriptionId: url,
        },
      }),
      invalidatesTags: ['ReportSubscriptions'],
    }),
    removeUnlinkedRecipients: builder.mutation<string, any>({
      query: (id) => ({
        url: `${environment.reportingServiceEndpoint}/api/v1/report-subscriptions/unlinkedrecipients/${id}`,
        method: 'DELETE',
        params: {
          subscriptionId: id,
        },
      }),
      invalidatesTags: ['ReportSubscriptions'],
    }),
    unsubscribeSubscriptions: builder.mutation<string, any>({
      query: (id) => ({
        url: `${environment.reportingServiceEndpoint}/api/v1/report-subscriptions/unsubscribe/${id}`,
        method: 'DELETE',
        params: {
          subscriptionId: id,
        },
      }),
      invalidatesTags: ['ReportSubscriptions'],
    }),
    unsubscribeFromAllSubscriptions: builder.mutation<string, any>({
      query: (id) => ({
        url: `${environment.reportingServiceEndpoint}/api/v1/staff/report-subscriptions/unsubscribe/${id}`,
        method: 'DELETE',
        params: {
          staffId: id,
        },
      }),
      invalidatesTags: ['ReportSubscriptions'],
    }),
    editFirmSubscriptions: builder.mutation<IPostReportSubscription, any>({
      query: (data) => ({
        url: '/api/v1/scheduled-reports/subscriptions',
        method: 'PATCH',
        data: data,
      }),
      invalidatesTags: ['ReportSubscriptions'],
    }),

    getStaffDetails: builder.query<IGetStaffDetails, string>({
      query: (url) => ({ url, method: 'GET' }),
    }),

    getMenu: builder.query<IMenuItem[], void>({
      query: () => ({ url: '/api/reportingmenuforuser', method: 'GET' }),
      transformResponse: (response: { ReportsMenu: any[] }) => {
        const menu = transformServerMenuToLocalMenuStructure(response.ReportsMenu);
        // filter the Exchanges and Settlements report of Dashboard Menu
        return menu.filter((item) => {
          return !(item.Name === 'DashboardMenu' && !item.SubMenuItems?.length);
        });
      },
      providesTags: ['Menu'],
    }),
    getFavouriteReports: builder.query<IFavouriteReportFromServer[], void>({
      query: (url) => ({ url: '/api/favouritereports', method: 'GET' }),
      transformResponse: (response: IFavouriteReportResponse) => {
        return response.FavouriteReports;
      },
      providesTags: ['FavouriteReport'],
    }),
    addFavouriteReport: builder.mutation<
      boolean,
      {
        ReportName: string;
      }
    >({
      query: ({ ...data }) => ({
        url: '/api/addtofavouritereports',
        method: 'POST',
        data: data,
      }),
      invalidatesTags: ['FavouriteReport'],
    }),
    deleteFavouriteReport: builder.mutation<boolean, any>({
      query: ({ ...data }) => ({
        url: '/api/removefavouritereport',
        method: 'POST',
        data: data,
      }),
      invalidatesTags: ['FavouriteReport'],
    }),
    getRecommendReports: builder.query<IRecommendReportFromServer[], void>({
      query: (url) => ({ url: '/api/v1/recommend-reports', method: 'GET' }),
      // transformResponse: (response: IRecommendReport[]) => {
      //     return response;
      // },
      providesTags: ['RecommendReport'],
    }),
    getFinancialYear: builder.query<{ FinancialYearStartMonth: number }, void>({
      query: () => ({ url: '/api/financialyear', method: 'GET' }),
    }),
    getFirmStaff: builder.query<StaffFilterDetail[], void>({
      query: () => ({ url: `api/filter/stafflistbystatus/false`, method: 'GET' }),
    }),
    getSubscriberList: builder.query<StaffFilterDetail[], string>({
      query: (id) => ({
        url: `${environment.reportingServiceEndpoint}/api/v1/report-recipients/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: { recipients: StaffFilterDetail[] }) => {
        return response.recipients;
      },
    }),
  }),
});
export const {
  useGetFirmSubscriptionsQuery,
  useCreateFirmSubscriptionsMutation,
  useEditFirmSubscriptionsMutation,
  useDeleteFirmSubscriptionsMutation,
  useGetStaffDetailsQuery,
  useGetAutoCompleteOptionListQuery,
  useGetAgeingPeriodQuery,
  useGetFinancialYearQuery,
  useGetMenuQuery,
  useGetFavouriteReportsQuery,
  useAddFavouriteReportMutation,
  useDeleteFavouriteReportMutation,
  useGetRecommendReportsQuery,
  useGetFirmStaffQuery,
  useGetSubscriptionsTemplateQuery,
  useGetSubscriberListQuery,
  useCreateSubscriptionsMutation,
  useUnsubscribeSubscriptionsMutation,
  useUnsubscribeFromAllSubscriptionsMutation,
  useRemoveUnlinkedRecipientsMutation,
} = reportingApi;
